import { useQueryClient } from '@tanstack/react-query'
import React, { useContext } from 'react'

import NeedsAttentionContext from '../../context/NeedsAttentionContext'
import {
  getTagsForBrokerageQueryKey,
  useGetTagsForBrokerage,
} from '../../hooks/needs_attention/useGetTagsForBrokerage'
import { useModifyTagArchivedStatus } from '../../hooks/needs_attention/useModifyTagArchivedStatus'
import AnimateHeightAndOpacity from '../../reusable/AnimateHeightAndOpacity'
import { Banner } from '../../reusable/Banner'
import LoadingSpinnerCentered from '../../reusable/LoadingSpinnerCentered'
import RotatingCircleRightChevron from '../../reusable/RotatingCircleRightChevron'
import { Button } from '../../ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../ui/card'
import { type Tag } from '../types'

type TagManagementProps = {
  setCreateOrEditTagSettings: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean
      tag: Tag | undefined
    }>
  >
}

const TagManagement = ({ setCreateOrEditTagSettings }: TagManagementProps) => {
  const { data: allTags, error, isLoading } = useGetTagsForBrokerage()
  const [isViewable, setIsViewable] = React.useState({
    temporary: true,
    permanent: true,
  })

  if (isLoading) {
    return <LoadingSpinnerCentered />
  }

  if (error) {
    return (
      <Banner type='error' titleText='Error loading tags' className='mt-4'>
        <div>{error.message}</div>
      </Banner>
    )
  }

  if (!allTags?.length) {
    return (
      <div className='font-semibold mt-4'>
        No tags have been created! Create one using the button above.
      </div>
    )
  }

  const { tags, tempTags } = allTags.reduce<Record<string, Tag[]>>(
    (acc, tag) => {
      return tag.brokerage_id
        ? { ...acc, tempTags: [...acc.tempTags, tag] }
        : { ...acc, tags: [...acc.tags, tag] }
    },
    { tags: [], tempTags: [] }
  )

  return (
    <div className='mt-4 space-y-4 overflow-visible'>
      <div>
        View all available tags below. Click on a tag to view more details /
        edit or archive temporary tags.
      </div>
      <div
        className='font-semibold flex items-center justify-start cursor-pointer'
        onClick={() =>
          setIsViewable((prev) => ({ ...prev, permanent: !prev.permanent }))}
      >
        <div className='mr-2'>Permanent Tags</div>
        <RotatingCircleRightChevron isOpen={isViewable.permanent} />
      </div>
      <AnimateHeightAndOpacity isOpen={isViewable.permanent}>
        <TagsOutput
          tags={tags}
          setCreateOrEditTagSettings={setCreateOrEditTagSettings}
        />
      </AnimateHeightAndOpacity>
      <hr className='my-4' />
      <div
        className='font-semibold flex items-center justify-start cursor-pointer'
        onClick={() =>
          setIsViewable((prev) => ({ ...prev, temporary: !prev.temporary }))}
      >
        <div className='mr-2'>Temporary Tags</div>
        <RotatingCircleRightChevron isOpen={isViewable.temporary} />
      </div>
      <AnimateHeightAndOpacity isOpen={isViewable.temporary}>
        <TagsOutput
          tags={tempTags}
          setCreateOrEditTagSettings={setCreateOrEditTagSettings}
          isTemporary
        />
      </AnimateHeightAndOpacity>
    </div>
  )
}

const TagsOutput = ({
  tags,
  isTemporary,
  setCreateOrEditTagSettings,
}: {
  tags: Tag[]
  isTemporary?: boolean
} & TagManagementProps) => {
  if (!tags?.length) {
    return (
      <div className='font-light'>
        No {isTemporary ? 'temporary' : 'permanent'} tags have been created!
      </div>
    )
  }

  return (
    <div className='space-y-4'>
      {tags?.map((tag) => {
        return (
          <TagOutput
            key={tag.id}
            tag={tag}
            setCreateOrEditTagSettings={setCreateOrEditTagSettings}
          />
        )
      })}
    </div>
  )
}

const TagOutput = ({
  tag,
  setCreateOrEditTagSettings,
}: { tag: Tag } & TagManagementProps) => {
  const queryClient = useQueryClient()
  const { isNAVAdmin } = useContext(NeedsAttentionContext)

  const [isOpen, setIsOpen] = React.useState(false)
  const modifyTagArchivedStatusMutation = useModifyTagArchivedStatus({
    id: tag.id,
  })

  const handleEditClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setCreateOrEditTagSettings({ isOpen: true, tag })
  }

  const handleArchiveClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    modifyTagArchivedStatusMutation.mutate(
      {
        archived: !tag.archived,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getTagsForBrokerageQueryKey(),
          })
        },
      }
    )
  }

  return (
    <Card
      key={tag.id}
      className='w-full cursor-pointer'
      onClick={() => setIsOpen((prev) => !prev)}
    >
      <CardHeader>
        <CardTitle>{tag.name}</CardTitle>
        <CardDescription>{tag.category}</CardDescription>
      </CardHeader>
      <AnimateHeightAndOpacity isOpen={isOpen}>
        <CardContent className='space-y-4'>
          <div>{tag.description}</div>
          {/* Only allow tags to be edited if 1) temporary or 2) NAV Admin user is viewing tag. */}
          {(isNAVAdmin || tag.brokerage_id) && (
            <div className='flex justify-end space-x-4'>
              <Button variant='outline' onClick={handleEditClick}>
                Edit
              </Button>
              <Button
                variant='destructive'
                onClick={handleArchiveClick}
                disabled={modifyTagArchivedStatusMutation.isPending}
              >
                {tag.archived ? 'Unarchive' : 'Archive'}
              </Button>
            </div>
          )}
        </CardContent>
      </AnimateHeightAndOpacity>
    </Card>
  )
}

export default TagManagement
